import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';

const ViolationSettings = ({ onClose }) => {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    allowed_roles: ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'],
    allowed_individuals: [],
    auto_email_notifications: true,
    severity_actions: {
      minor: {
        action: 'warning_email',
        description: 'Send warning email with chance to respond',
        followup_days: 7,
        escalation: 'reminder'
      },
      moderate: {
        action: 'warning_with_hearing_notice',
        description: 'Send warning with 14-day hearing notice unless compliant',
        followup_days: 14,
        escalation: 'hearing_scheduled'
      },
      severe: {
        action: 'immediate_hearing',
        description: 'Schedule immediate hearing with fine notice',
        followup_days: 7,
        escalation: 'fine_issued',
        requires_meeting_link: true
      },
      critical: {
        action: 'immediate_action',
        description: 'Immediate board action required with emergency protocols',
        followup_days: 3,
        escalation: 'emergency_measures',
        requires_meeting_link: true
      }
    },
    hearing_request_settings: {
      homeowner_request_behavior: 'no_action',
      board_schedule_behavior: 'pause_payments_and_due_dates',
      auto_unpause_on_resolution: true,
      auto_unpause_on_fine_set: true
    }
  });
  
  const [homeowners, setHomeowners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    loadHomeowners();
  }, [user?.hoa_id]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('hoa_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/violations/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-HOA-ID': user?.hoa_id
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHomeowners = async () => {
    try {
      const token = localStorage.getItem('hoa_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hoa/members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const members = data.members || [];
        
        // Filter valid members
        const validMembers = members.filter(member => {
          // Must have email and be approved
          if (!member.email || member.verification_status !== 'approved') {
            return false;
          }
          
          const memberName = (member.name || '').toLowerCase();
          const email = (member.email || '').toLowerCase();
          
          // Block phantom and test users
          if (memberName.includes('john homeowner') || 
              memberName.includes('jane resident') ||
              email.includes('test.com') || 
              email.includes('example.com') ||
              email.includes('test_user')) {
            return false;
          }
          
          return true;
        });
        
        // Add display names for users without names
        const formattedMembers = validMembers.map(m => {
          let displayName = m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim();
          if (!displayName) {
            displayName = m.email.split('@')[0];
          }
          return { ...m, name: displayName };
        });
        
        console.log('🔍 ViolationSettings - Valid members loaded:', formattedMembers.length);
        setHomeowners(formattedMembers);
      } else {
        console.error('❌ ViolationSettings - Failed to load members:', response.status);
      }
    } catch (error) {
      console.error('❌ ViolationSettings - Error loading members:', error);
    }
  };

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('hoa_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/violations/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-HOA-ID': user?.hoa_id
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        alert('Settings saved successfully!');
        onClose();
      } else {
        alert('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please check your connection.');
    }
  };

  const addIndividualAccess = (homeownerId) => {
    if (!settings.allowed_individuals.includes(homeownerId)) {
      setSettings(prev => ({
        ...prev,
        allowed_individuals: [...prev.allowed_individuals, homeownerId]
      }));
    }
    setSearchTerm('');
  };

  const removeIndividualAccess = (homeownerId) => {
    setSettings(prev => ({
      ...prev,
      allowed_individuals: prev.allowed_individuals.filter(id => id !== homeownerId)
    }));
  };

  const updateSeverityAction = (severity, field, value) => {
    setSettings(prev => ({
      ...prev,
      severity_actions: {
        ...prev.severity_actions,
        [severity]: {
          ...prev.severity_actions[severity],
          [field]: value
        }
      }
    }));
  };

  const accessOptions = [
    { value: 'board_only', label: 'Board Members Only', roles: ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'] },
    { value: 'security_board', label: 'Security & Board', roles: ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'security'] },
    { value: 'security_board_fine', label: 'Security, Board & Fine Committee', roles: ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'security', 'fine_committee'] },
    { value: 'security_board_arc', label: 'Security, Board & ARC Committee', roles: ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'security', 'arc_committee'] },
    { value: 'custom', label: 'Custom Selection', roles: [] }
  ];

  const filteredHomeowners = homeowners.filter(h => {
    const name = h.name || h.first_name + ' ' + h.last_name || '';
    const email = h.email || '';
    const searchLower = searchTerm.toLowerCase();
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-center mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Violation Settings</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Access Control */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Who Can Report Violations</h3>
            
            <div className="space-y-3">
              {accessOptions.map(option => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="access_level"
                    value={option.value}
                    checked={JSON.stringify(settings.allowed_roles.sort()) === JSON.stringify(option.roles.sort())}
                    onChange={() => {
                      if (option.value !== 'custom') {
                        setSettings(prev => ({ ...prev, allowed_roles: option.roles }));
                      }
                    }}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
            
            {/* Individual Access */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-800 mb-3">Additional Individual Access</h4>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search homeowners..."
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                />
              </div>
              
              {searchTerm && (
                <div className="border border-gray-200 rounded max-h-32 overflow-y-auto mb-3">
                  {filteredHomeowners.map(homeowner => (
                    <button
                      key={homeowner.id}
                      onClick={() => addIndividualAccess(homeowner.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="text-sm font-medium">{homeowner.name}</div>
                      <div className="text-xs text-gray-500">{homeowner.email}</div>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="flex flex-wrap gap-2">
                {settings.allowed_individuals.map(homeownerId => {
                  const homeowner = homeowners.find(h => h.id === homeownerId);
                  return homeowner ? (
                    <span key={homeownerId} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {homeowner.name}
                      <button
                        onClick={() => removeIndividualAccess(homeownerId)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          {/* Severity Actions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Severity-Based Actions</h3>
            
            <div className="space-y-6">
              {Object.entries(settings.severity_actions).map(([severity, action]) => (
                <div key={severity} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-md font-semibold text-gray-800 capitalize">{severity} Violations</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      severity === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                      severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                      severity === 'severe' ? 'bg-red-100 text-red-800' :
                      'bg-red-200 text-red-900'
                    }`}>
                      {severity.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Action Description</label>
                      <textarea
                        value={action.description}
                        onChange={(e) => updateSeverityAction(severity, 'description', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        rows="2"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Days</label>
                        <input
                          type="number"
                          value={action.followup_days}
                          onChange={(e) => updateSeverityAction(severity, 'followup_days', parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                          min="1"
                          max="30"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Escalation Action</label>
                        <select
                          value={action.escalation}
                          onChange={(e) => updateSeverityAction(severity, 'escalation', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                          <option value="reminder">Send Reminder</option>
                          <option value="hearing_scheduled">Schedule Hearing</option>
                          <option value="fine_issued">Issue Fine</option>
                          <option value="emergency_measures">Emergency Measures</option>
                        </select>
                      </div>
                    </div>
                    
                    {(severity === 'severe' || severity === 'critical') && (
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={action.requires_meeting_link || false}
                            onChange={(e) => updateSeverityAction(severity, 'requires_meeting_link', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Requires meeting minutes documentation</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Email Notifications */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Email Notification Settings</h3>
            
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.auto_email_notifications}
                  onChange={(e) => setSettings(prev => ({ ...prev, auto_email_notifications: e.target.checked }))}
                  className="mr-3"
                />
                <span className="text-sm text-gray-700">Automatically send email notifications to homeowners</span>
              </label>
            </div>
          </div>

          {/* Hearing Request Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hearing Request & Scheduling Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When homeowner requests hearing:
                </label>
                <select
                  value={settings.hearing_request_settings?.homeowner_request_behavior || 'no_action'}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    hearing_request_settings: {
                      ...prev.hearing_request_settings,
                      homeowner_request_behavior: e.target.value
                    }
                  }))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="no_action">Do nothing (board decides next steps)</option>
                  <option value="pause_all_actions">Pause all actions until board schedules hearing</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls what happens when a homeowner requests a hearing for their violation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When board schedules hearing:
                </label>
                <select
                  value={settings.hearing_request_settings?.board_schedule_behavior || 'pause_payments_and_due_dates'}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    hearing_request_settings: {
                      ...prev.hearing_request_settings,
                      board_schedule_behavior: e.target.value
                    }
                  }))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="pause_payments_and_due_dates">Pause payment reminders and due dates</option>
                  <option value="no_pause">Continue payment reminders and due dates</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls what happens when the board officially schedules a hearing
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.hearing_request_settings?.auto_unpause_on_resolution || true}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      hearing_request_settings: {
                        ...prev.hearing_request_settings,
                        auto_unpause_on_resolution: e.target.checked
                      }
                    }))}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Auto-unpause when violation is resolved</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.hearing_request_settings?.auto_unpause_on_fine_set || true}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      hearing_request_settings: {
                        ...prev.hearing_request_settings,
                        auto_unpause_on_fine_set: e.target.checked
                      }
                    }))}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">Auto-unpause when fine is set or updated</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={saveSettings}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViolationSettings;